// public/v3/src/components/eligibility-panel.jsx
// Phase 1 Elation eligibility status panel: usage + GET-only refresh.

import React, { useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';
import {
  eligibilityInsuranceId,
  isMedicaidInsurance,
  isMedicareInsurance,
  qmbCheckedAt,
} from '../lib/eligibility-infer';
import { eligibilitySummaryModel, rowDetail, statusClass, payerLabel } from '../lib/eligibility-view';

const STALE_MS = 7 * 24 * 60 * 60 * 1000;
const FEATURE_UNAVAILABLE_MESSAGE = 'Eligibility refresh is not enabled for this environment yet.';

function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return String(iso).slice(0, 10);
  return d.toLocaleDateString();
}

function hasRelevantInsurance(patient) {
  const ins = (patient && patient._raw && patient._raw.insurances) || [];
  return ins.some(i => eligibilityInsuranceId(i) && (isMedicareInsurance(i) || isMedicaidInsurance(i)));
}

function hasElationPatientId(patient) {
  const raw = (patient && patient._raw) || {};
  return !!(raw.elation_id || raw.elationId || raw.elationPatientId);
}

function canAttemptRefresh(patient) {
  return hasRelevantInsurance(patient) || hasElationPatientId(patient);
}

function isCacheStale(patient) {
  const qmb = patient && patient._raw && patient._raw.qmb;
  if (qmb && qmb.source === 'manual') return false;
  const checkedAt = qmbCheckedAt(qmb);
  if (!checkedAt) return true;
  const ts = new Date(checkedAt).getTime();
  if (!Number.isFinite(ts)) return true;
  return Date.now() - ts >= STALE_MS;
}

export function EligibilityPanel({ patient, onRefreshed }) {
  const [busy, setBusy] = useState(false);
  const [usage, setUsage] = useState(null);
  const [error, setError] = useState(null);
  const [featureUnavailable, setFeatureUnavailable] = useState(false);
  const [featureUnavailableMessage, setFeatureUnavailableMessage] = useState('');
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const autoTried = useRef(new Set());

  function markFeatureUnavailable(e) {
    setFeatureUnavailable(true);
    setFeatureUnavailableMessage(e?.message || FEATURE_UNAVAILABLE_MESSAGE);
  }

  async function refreshFromElation() {
    if (!patient || !patient.id) return;
    setBusy(true);
    setError(null);
    try {
      const result = await api.post(`/api/eligibility/refresh/${patient.id}`);
      onRefreshed?.(result);
    } catch (e) {
      if (e && e.status === 503) {
        markFeatureUnavailable(e);
      } else {
        setError(e?.message || 'Eligibility refresh failed');
      }
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    let cancelled = false;
    api.get('/api/eligibility/usage')
      .then(r => { if (!cancelled) setUsage(r && r.usage); })
      .catch((e) => {
        if (!cancelled && e && e.status === 503) markFeatureUnavailable(e);
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!patient || !patient.id || featureUnavailable) return;
    if (autoTried.current.has(patient.id)) return;
    if (!canAttemptRefresh(patient)) return;
    if (!isCacheStale(patient)) return;
    autoTried.current.add(patient.id);
    refreshFromElation();
    // refreshFromElation intentionally omitted; it depends on current patient.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patient && patient.id, featureUnavailable]);

  if (!patient || !patient._raw) return null;

  if (featureUnavailable) {
    return (
      <div className="eligibility-panel">
        <div className="eligibility-status-head">
          <strong>Eligibility per Elation</strong>
        </div>
        <div className="eligibility-empty">
          {featureUnavailableMessage || FEATURE_UNAVAILABLE_MESSAGE}
        </div>
      </div>
    );
  }

  const model = eligibilitySummaryModel(patient);
  if (!model.available) return null;

  function copyCodes() {
    try {
      navigator.clipboard.writeText(model.errorCodes.join(', '));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_) {}
  }

  return (
    <div className="eligibility-summary">
      <button
        type="button"
        className="eligibility-summary-line"
        aria-expanded={open}
        onClick={() => setOpen(o => !o)}>
        <span className="elig-payer">{model.payer}</span>
        <span className={`elig-chip ${model.statusClass}`}>
          {model.status === 'error' ? <span aria-hidden="true">⚠ </span> : null}{model.statusLabel}
        </span>
        <span className="elig-toggle">details {open ? '▴' : '▾'}</span>
      </button>

      {open && (
        <div className="eligibility-summary-detail">
          {model.checkedAt && <div className="elig-meta">Last checked {formatDate(model.checkedAt)}</div>}
          {model.hasRows ? (
            <ul className="eligibility-status-list">
              {model.rows.map(({ insurance, latest }) => (
                <li key={eligibilityInsuranceId(insurance)} className="eligibility-status-row">
                  <span className="ins-name">{payerLabel(insurance)}</span>
                  <span className={`ins-status ${statusClass(latest ? latest.status : 'no check')}`}>{latest ? latest.status : 'no check'}</span>
                  <span className="ins-type">{rowDetail(latest)}</span>
                </li>
              ))}
            </ul>
          ) : (
            <div className="eligibility-empty">
              {hasElationPatientId(patient)
                ? 'No Medicare/Medicaid insurance id synced locally yet.'
                : 'No Medicare/Medicaid insurance id on file.'}
            </div>
          )}

          <div className="eligibility-conclusion">{model.conclusion}</div>

          {model.errorCodes.length > 0 && (
            <div className="eligibility-error-codes">
              <code>{model.errorCodes.join(', ')}</code>
              <button type="button" className="link-btn" onClick={copyCodes}>{copied ? 'copied' : 'copy codes'}</button>
            </div>
          )}

          {usage ? (
            <div className="eligibility-quota">
              <span className="quota-badge">This month: {Number(usage.total_checks || usage.checks || 0)} checks used</span>
            </div>
          ) : null}

          {error ? <div className="eligibility-error">{error}</div> : null}

          <div className="eligibility-actions">
            <button className="btn sm" disabled={busy || !canAttemptRefresh(patient)} onClick={refreshFromElation}>
              {busy ? 'Refreshing...' : 'Refresh from Elation'}
            </button>
            <button className="btn sm" disabled title="Phase 2: runs a new 270/271 transaction and uses 1 credit per insurance">
              Run new eligibility check
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
